<script setup lang="ts">
import { computed } from "vue"
import { UploadPhase } from "./Uploads"
import { PhotoIcon, ExclamationTriangleIcon, CheckIcon } from "@heroicons/vue/24/outline"

const props = withDefaults(defineProps<{
  id: string
  filename: string
  preview?: string
  phase: UploadPhase
  progress?: number
  selected: boolean
  active: boolean
  dirty: boolean
  error?: string
  // Rows for in-flight uploads have no media id yet — show their progress but
  // don't allow selecting/activating them until the upload completes.
  ready?: boolean
}>(), {
  ready: true
})

const emit = defineEmits<{
  "toggle-select": [id: string]
  "activate": [id: string]
}>()

const percent = computed(() => props.progress !== undefined ? Math.round(props.progress * 100) : 0)

const classes = computed(() => {
  const base = ["flex items-center gap-3 px-3 py-2 border-b cursor-pointer w-full min-w-0 overflow-hidden"]
  if (props.active) base.push("ring-2", "ring-primary", "bg-primary/5")
  return base
})
</script>

<template>
  <div :class="classes">
    <input
      type="checkbox"
      class="checkbox checkbox-sm"
      data-testid="row-checkbox"
      :checked="selected"
      :disabled="!ready"
      @click.stop
      @change="emit('toggle-select', id)"
    >

    <div
      data-testid="row-body"
      class="flex items-center gap-3 flex-1 min-w-0"
      @click="ready && emit('activate', id)"
    >
      <img
        v-if="preview"
        :src="preview"
        alt="Vorschau"
        class="w-16 h-16 object-contain bg-gray-100 rounded shrink-0"
        data-testid="row-preview"
      >
      <PhotoIcon v-else class="w-16 h-16 text-gray-300 shrink-0"/>

      <div class="flex-1 min-w-0">
        <div class="flex items-center gap-2">
          <span class="font-medium truncate">{{ filename }}</span>
          <span
            v-if="dirty"
            data-testid="row-dirty"
            class="inline-block w-2 h-2 rounded-full bg-amber-500"
            title="Ungespeicherte Änderungen"
          />
        </div>

        <div data-testid="row-status" class="text-xs text-gray-500">
          <template v-if="phase === UploadPhase.PREPARING">Analyse läuft …</template>
          <template v-else-if="phase === UploadPhase.CREATING">Upload wird vorbereitet …</template>
          <template v-else-if="phase === UploadPhase.UPLOADING">{{ percent }} %</template>
          <span
            v-else-if="phase === UploadPhase.COMPLETE"
            data-testid="row-status-complete"
            class="text-green-600 inline-flex items-center gap-1"
          >
            <CheckIcon class="w-4 h-4"/> Fertig
          </span>
          <span
            v-else-if="phase === UploadPhase.FAILED"
            data-testid="row-status-error"
            class="text-red-600 inline-flex items-center gap-1"
            :title="error"
          >
            <ExclamationTriangleIcon class="w-4 h-4"/> Fehler
          </span>
        </div>
      </div>
    </div>
  </div>
</template>
